/* What the skill should teach, said once, then a Definable chat that drafts it — the
   same chat as anywhere else, opened with this as its first message. Definable shows
   the draft on an approval card before anything is saved. */
import React, { useState } from "react";
import { WandSparkles } from "lucide-react";

import { TextArea } from "@/shared/components/settings-atoms";
import { BrandButton, GhostButton } from "@/shared/components/ui-kit";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/shared/components/ui/dialog";

const STARTERS = [
  "Our release notes: a one-line summary, then New, Better and Fixes, each linking to the docs.",
  "How we triage a support ticket: product area, urgency, then the queue it goes to.",
  "Our brand voice: plain words, short sentences, no hype.",
];

/** The chat's first message: the ask, and what Definable should do with it. */
export const askPrompt = (said: string) =>
  `Write a skill for our workspace. ${said.trim()}\n\nDraft the SKILL.md and any files it needs, and show me before you save it.`;

export default function AskDefinableDialog({ onAsk, onClose }: {
  onAsk: (prompt: string) => void;
  onClose: () => void;
}) {
  const [said, setSaid] = useState("");
  const ready = said.trim().length > 0;
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="p-0 gap-0 w-[480px] max-w-[94vw] rounded-[16px] overflow-hidden bg-[var(--canvas)] border-[var(--hairline-strong)] shadow-dialog">
        <div className="px-5 pt-5 pb-3 pr-12 flex items-start gap-3">
          <div className="w-8 h-8 shrink-0 rounded-[8px] bg-[var(--surface-2)] flex items-center justify-center">
            <WandSparkles className="w-4 h-4 text-[var(--text-secondary)]" strokeWidth={1.75} />
          </div>
          <div className="min-w-0">
            <DialogTitle className="text-[15px] font-semibold tracking-[-0.01em] text-[var(--text-primary)]">Ask Definable to write a skill</DialogTitle>
            <DialogDescription className="text-[13px] leading-[1.5] text-[var(--text-tertiary)] mt-0.5">
              Describe the procedure. Definable drafts it and shows you the SKILL.md before it saves anything.
            </DialogDescription>
          </div>
        </div>
        <form
          onSubmit={(e) => { e.preventDefault(); if (ready) onAsk(askPrompt(said)); }}
          className="px-5 pb-5 space-y-3"
        >
          <TextArea
            autoFocus
            value={said}
            onChange={setSaid}
            rows={4}
            aria-label="What the skill should teach"
            placeholder="What should the skill teach an agent to do?"
            className="resize-none py-2.5 text-[16px] sm:text-[13.5px]"
          />
          <div className="flex flex-wrap gap-1.5">
            {STARTERS.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setSaid(s)}
                className="press h-8 max-w-full px-3 rounded-full flex items-center bg-[var(--surface-2)] hover:bg-[var(--surface-3)] text-[12px] text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
              >
                <span className="truncate">{s.split(":")[0]}</span>
              </button>
            ))}
          </div>
          <div className="flex items-center justify-end gap-2 pt-1">
            <GhostButton type="button" onClick={onClose}>Cancel</GhostButton>
            <BrandButton type="submit" disabled={!ready} className="disabled:opacity-40 disabled:pointer-events-none">Open in Definable</BrandButton>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
